import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, List, ListItem, IconButton, Paper, Button, MenuItem, Select, FormControl, InputLabel, TextField, Tooltip } from '@mui/material';
import { TaskAlt, AddTask } from '@mui/icons-material';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'; // Adapter for Moment.js
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import { convertTime } from 'src/utils/time';

function AttendanceModal(props) {
    const [attendance, setAttendance] = useState([]);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskType, setTaskType] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [preAuthUnits, setPreAuthUnits] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submittingSessions, setSubmittingSessions] = useState(new Set());

    useEffect(() => {
        if (props.open) {
            let sessions = props.sessions.filter(session => session.schedule_id === props.schedule?.schedule_id);            
            setAttendance(sessions);
            setTaskType(props.schedule.task_type);
            setStartTime(moment(props.schedule.start_time));
            setEndTime(moment(props.schedule.end_time));
            console.log('AttendanceModal opened with schedule:', props.schedule);
        } else {
            setAttendance([]);
        }
    }, [props.open, props.sessions, props.schedule]);

    const handleTaskAction = async (session, useNewValues = false, checkin = true) => {
        // Added prospect check
        let customer = props.customers.find(cus => cus.cus_id === session.customer_id);
        if (customer.cus_status === 'Prospect' &&  !customer.projects) {
            alert('You will not be able to add a note or associate a form to a prospect. Please create an active project for this customer and then mark the attendance');
            return;
        }
        if (useNewValues && ((startTime && !moment(startTime).isValid()) || (endTime && !moment(endTime).isValid()))) {
            props.onError("Please enter a valid start and end time");
            return;
        }
        if (useNewValues && endTime && startTime && moment(endTime).isBefore(moment(startTime))) {
            props.onError("End time must be after start time");
            return;
        }

        // Prevent duplicate submissions for the same session
        if (submittingSessions.has(session.session_id)) return;
        setSubmittingSessions(prev => new Set(prev).add(session.session_id));
        setSubmitting(true);

        try {
            const existingTaskId = session.task_id || selectedTask?.tas_id;
            console.log('Handling task action for session:', session, 'Existing Task ID:', existingTaskId, 'Check-in:', checkin);
            if (existingTaskId) {
                const payload = {
                    tas_id: existingTaskId,
                    tas_is_active: checkin ? 1 : 0,
                    tas_type: checkin ? taskType : null,
                    tas_title: checkin ? props.schedule.schedule_name : undefined,
                    tas_start_time: checkin && startTime ? moment(startTime).format('YYYY-MM-DD HH:mm:ss') : null,
                    tas_end_time: checkin && endTime ? moment(endTime).format('YYYY-MM-DD HH:mm:ss') : null,
                    check_in_time: checkin ? props.schedule.start_time : null,
                    check_out_time: checkin ? props.schedule.end_time : null,
                    task_id: existingTaskId,
                    cus_id: session.customer_id,
                    emp_id: props.user.emp_id,
                    company_id: props.user.comp_id,
                    empLocate_timezone: moment.tz.guess()
                };
                await axios.put(APIURL + '/sessions/attendance/' + session.session_id, payload);
            } else {
                // Check server for an existing task before creating a new one.
                // Look up by session_id — avoids matching tasks from other sessions
                // that share the same schedule name.
                let foundTaskId = null;
                try {
                    const sessionTaskRes = await axios.get(APIURL + '/task/session', {
                        params: { session_id: session.session_id }
                    });
                    if (sessionTaskRes.data?.data) {
                        foundTaskId = sessionTaskRes.data.data.tas_id;
                    }
                } catch {}

                if (foundTaskId) {
                    // Task already exists — update it instead of creating a duplicate
                    const payload = {
                        tas_id: foundTaskId,
                        tas_is_active: checkin ? 1 : 0,
                        tas_type: checkin ? (taskType || props.schedule.task_type) : null,
                        tas_title: checkin ? props.schedule.schedule_name : undefined,
                        tas_start_time: checkin && startTime ? moment(startTime).format('YYYY-MM-DD HH:mm:ss') : null,
                        tas_end_time: checkin && endTime ? moment(endTime).format('YYYY-MM-DD HH:mm:ss') : null,
                        check_in_time: checkin ? props.schedule.start_time : null,
                        check_out_time: checkin ? props.schedule.end_time : null,
                        task_id: foundTaskId,
                        cus_id: session.customer_id,
                        emp_id: props.user.emp_id,
                        company_id: props.user.comp_id,
                        empLocate_timezone: moment.tz.guess()
                    };
                    await axios.put(APIURL + '/sessions/attendance/' + session.session_id, payload);
                } else {
                    await createTask(session, checkin);
                }
            }

            setTaskModalOpen(false);
            setSelectedSession(null);
            setSelectedTask(null);
            setTaskType('');
            setStartTime(null);
            setEndTime(null);

            props.reloadSessionData();
            if(props.getActiveSessions)
                props.getActiveSessions();
            props.onSuccess(checkin ? "Attendance marked successfully" : "Attendance unmarked successfully");

        } catch (error) {
            console.log(error);
            props.onError("An error occurred while marking attendance");
        } finally {
            setSubmitting(false);
            setSubmittingSessions(prev => {
                const next = new Set(prev);
                next.delete(session.session_id);
                return next;
            });
        }
    };

    const createTask = async (session, checkin) => {
        try {
            const projData = await axios.get(APIURL + '/project', { params: { cus_id: session.customer_id } });
            const proj = projData.data.find(p => p.pro_is_active) || projData.data[0];
            const loc = props.resources?.find(r => r.resource_id === props.schedule.resource_id);
            console.log('Creating task for session:', session, 'Project:', proj, 'Location:', loc);
            
            const taskStartTime = moment(startTime || props.schedule.start_time).format('YYYY-MM-DD HH:mm:ss')
            const taskEndTime = moment(endTime || props.schedule.end_time).format('YYYY-MM-DD HH:mm:ss')
            const payload = {
                company_id: props.comp_id,
                tTitle: props.schedule.schedule_name,
                tDesc: 'Session Task',
                tType: taskType || props.schedule.task_type,
                tStrtDate: taskStartTime,
                tEndDate: taskEndTime,
                tStartTime: convertTime(taskStartTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DD HH:mm:ss') ?? taskStartTime,
                tEndTime: convertTime(taskEndTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DD HH:mm:ss') ?? taskEndTime,
                tPriority: 'Low',
                tIsBillable: props.user.comp_settings.find(s => s.feature_id === 56)?.feature_value === 'true',
                tNotes: session.notes || '',
                tProject: proj.pro_id,
                tLoc: loc?.location_id || null,
                tLoc1: null,
                tLoc2: null,
                tasMmId: null,
                tasRequiredSig: false,
                cusId: session.customer_id,
                user_id: props.user.emp_id,
                projId: proj.pro_id,
                empId: null,
                locId: loc?.location_id || null,
                emploc_lat: loc?.loc_lat || null,
                emploc_lon: loc?.loc_lon || null,
                start_time: taskStartTime,
                end_time: taskEndTime,
                session_id: session.session_id,
                cus_id: session.customer_id,
                timesheet_status: 0,
                task_emp_timezone: props.user.emp_timezone || '',
                empLocate_timezone: moment.tz.guess()
            };
            await axios.post(APIURL + '/sessions/mark', payload);
        } catch (error) {
            // Propagate original error to display server message
            throw error;
        }
    };

    const handleOpenTaskModal = async (session) => {
        setSelectedSession(session);

        let foundTask = null;

        // Primary: load by task_id if the session already has one
        if (session.task_id) {
            const task = await axios.get(APIURL + '/task', { params: { taskId: session.task_id } });
            if (task.data?.length > 0) {
                foundTask = task.data[0];
            }
        }

        // Fallback: look up by session_id so we always reuse an existing task even
        // when the local session object hasn't been refreshed yet or task_id is null.
        if (!foundTask) {
            try {
                const sessionTaskRes = await axios.get(APIURL + '/task/session', {
                    params: { session_id: session.session_id }
                });
                if (sessionTaskRes.data?.data) {
                    foundTask = sessionTaskRes.data.data;
                }
            } catch {}
        }

        let typeToUse;
        if (foundTask) {
            setSelectedTask(foundTask);
            typeToUse = foundTask.tas_type || props.schedule.task_type;
            setTaskType(typeToUse);
            setStartTime(foundTask.tas_start_time ? moment(foundTask.tas_start_time) : moment(props.schedule.start_time));
            setEndTime(foundTask.tas_end_time ? moment(foundTask.tas_end_time) : moment(props.schedule.end_time));
        } else {
            typeToUse = props.schedule.task_type;
            setTaskType(typeToUse);
            setStartTime(moment(props.schedule.start_time));
            setEndTime(moment(props.schedule.end_time));
        }
        // fetch pre-auth units for this customer and task type (based on the current type)
        if (typeToUse) {
            axios.get(APIURL + '/preAuth/taskType', { 
                params: { 
                    companyID: props.comp_id, 
                    taskType: typeToUse,
                    fromDate: moment(startTime).isValid() ? moment(startTime).subtract(2, 'months').format('MM/DD/YYYY') : null,
                    toDate: moment(startTime).isValid() ? moment(startTime).endOf("day").format('MM/DD/YYYY') : null
                } 
            })
                .then(response => {
                    const entry = response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).find(item => item.cus_id === session.customer_id);
                    setPreAuthUnits(entry ? Math.max(0, entry.remainingUnits) : null);
                })
                .catch(() => setPreAuthUnits(null));
        }
        setTaskModalOpen(true);
    };

    useEffect(() => {
        if (!taskModalOpen || !selectedSession || !taskType) return;
        console.log(taskType);
        
        axios.get(APIURL + '/preAuth/taskType', { 
            params: { 
                companyID: props.comp_id, 
                taskType,
                fromDate: moment(startTime).isValid() ? moment(startTime).subtract(2, 'months').format('MM/DD/YYYY') : null,
                toDate: moment(startTime).isValid() ? moment(startTime).endOf("day").format('MM/DD/YYYY') : null
            } 
        })
            .then(response => {
                console.log(response, selectedSession );
                
                const entry = response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).find(item => item.cus_id === selectedSession.customer_id);
                setPreAuthUnits(entry ? Math.max(0, entry.remainingUnits) : null);
                console.log(entry);
                
            })
            .catch(() => setPreAuthUnits(null));
    }, [taskType, taskModalOpen, selectedSession, props.comp_id]);

    return (
        <>
            <Modal enforceFocus={false}
                open={props.open}
                onClose={props.onClose}
                aria-labelledby="attendance-modal-title"
                aria-describedby="attendance-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '80vh',
                    overflow: 'hidden',
                }}>
                    <Typography id="attendance-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Attendance
                    </Typography>
                    <Paper sx={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        pr: 1,
                    }}>
                        <List dense>
                            {attendance.length > 0 ? attendance.map((session, index) => (
                                <ListItem key={index} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Tooltip title={session.check_in_time !== null ? "Unmark Attendance" : "Mark Attendance"} placement='top' >
                                        <IconButton
                                            disabled={
                                                !props.user.permissions.MarkAttendance ||
                                                (submitting || submittingSessions.has(session.session_id)) // Only disable for mark, not unmark
                                            }
                                            onClick={() => handleTaskAction(session, false, !session.check_in_time)}
                                        >
                                            {session.check_in_time !== null ? (
                                                <TaskAlt sx={{ color: 'rgb(63, 167, 214)' }} />
                                            ) : (
                                                <TaskAlt />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={"Modify Task"} placement='top'>
                                        <IconButton disabled={!props.user.permissions.ModifySessionTask || session.check_in_time || submitting || submittingSessions.has(session.session_id)} onClick={() => handleOpenTaskModal(session)}>
                                            <AddTask />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography sx={{ flex: 1, textAlign: 'right', pr: 2 }}>
                                        {session.cus_name}
                                    </Typography>
                                </ListItem>
                            )) : (
                                <ListItem>
                                    <Typography variant="body2" color="text.secondary">
                                        No Customers Assigned
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </Paper>
                </Box>
            </Modal>

            {/* Task Modification Modal */}
            <Modal enforceFocus={false}
                open={taskModalOpen}
                onClose={() => {
                    setTaskModalOpen(false);
                    setSelectedSession(null);
                    setSelectedTask(null);
                    setTaskType(props.schedule.tas_type);
                    setStartTime(moment(props.schedule.start_time));
                    setEndTime(moment(props.schedule.end_time));
                    setPreAuthUnits(null);
                }}
                aria-labelledby="task-modal-title"
                aria-describedby="task-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <Typography id="task-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Modify Task
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="task-type-label">Task Type</InputLabel>
                        <Select
                            labelId="task-type-label"
                            value={taskType}
                            label="Task Type"
                            onChange={(e) => setTaskType(e.target.value)}
                        >
                        {props.taskTypes?.map((taskType, index) => (
                            <MenuItem key={index} value={taskType}>
                                {taskType}
                            </MenuItem>                        
                        ))}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <TimePicker
                            label="Start Time"
                            value={moment(startTime).isValid() ? moment(startTime) : null}
                            onChange={(newTime) => {
                                if (newTime && moment(newTime).isValid()) {
                                    const base = moment(startTime).isValid() ? moment(startTime) : moment(props.schedule.start_time);
                                    setStartTime(base.set({ hour: newTime.hour(), minute: newTime.minute(), second: newTime.second() }));
                                } else {
                                    setStartTime(newTime);
                                }
                            }}
                            sx={{ mb: 2, width: '100%' }}
                        />
                        <TimePicker
                            label="End Time"
                            value={moment(endTime).isValid() ? moment(endTime) : null}
                            onChange={(newTime) => {
                                if (newTime && moment(newTime).isValid()) {
                                    const base = moment(endTime).isValid() ? moment(endTime) : moment(props.schedule.end_time);
                                    setEndTime(base.set({ hour: newTime.hour(), minute: newTime.minute(), second: newTime.second() }));
                                } else {
                                    setEndTime(newTime);
                                }
                            }}
                            sx={{ mb: 2, width: '100%' }}
                        />
                    </LocalizationProvider>
                    {preAuthUnits != null && (
                        <TextField
                            label="Available Units"
                            value={preAuthUnits}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 2 }}
                        />
                    )}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleTaskAction(selectedSession, true)}
                        disabled={submitting || (!taskType && (!startTime || !endTime))}
                    >
                        Mark Attendance
                    </Button>
                </Box>
            </Modal>
        </>
    );
}

export default AttendanceModal;
